import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, RefreshCw, Users, AlertTriangle } from 'lucide-react';

interface BranchItem {
  id: string;
  name: string;
  company?: { name: string };
}

interface HotspotUser {
  id: string;
  username: string;
  profile: string;
  limitBytesTotal?: number | null;
  bytesToday?: number | null;
  comment?: string | null;
  disabled?: boolean | null;
}

interface HotspotProfile {
  name: string;
}

interface DailyConsumptionItem {
  username: string;
  bytesToday: string | number;
}

type ModalMode = 'create' | 'edit';

const limitTotalRegex = /^\d+(K|M|GB)$/i;

const humanToBytes = (value: string) => {
  const raw = value.trim().toUpperCase();
  if (!raw) return null;
  if (!limitTotalRegex.test(raw)) return undefined;

  const unit = raw.endsWith('GB') ? 'GB' : raw.slice(-1);
  const numberPart = unit === 'GB' ? raw.slice(0, -2) : raw.slice(0, -1);
  const amount = Number(numberPart);
  if (!Number.isFinite(amount) || amount <= 0) return undefined;

  const multiplier = unit === 'GB' ? 1024 ** 3 : unit === 'M' ? 1024 ** 2 : 1024;
  return Math.floor(amount * multiplier);
};

const bytesToHumanInput = (value: number | null | undefined) => {
  if (!Number.isFinite(value) || !value || value <= 0) return '';
  if (value % (1024 ** 3) === 0) return `${Math.floor(value / (1024 ** 3))}GB`;
  if (value % (1024 ** 2) === 0) return `${Math.floor(value / (1024 ** 2))}M`;
  if (value % 1024 === 0) return `${Math.floor(value / 1024)}K`;
  return '';
};

const bytesToHumanDisplay = (value: number | null | undefined) => {
  if (!Number.isFinite(value) || !value || value <= 0) return '—';
  if (value % (1024 ** 3) === 0) return `${Math.floor(value / (1024 ** 3))}GB`;
  if (value % (1024 ** 2) === 0) return `${Math.floor(value / (1024 ** 2))}M`;
  if (value % 1024 === 0) return `${Math.floor(value / 1024)}K`;
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(2)}GB`;
  if (value >= 1024 ** 2) return `${Math.round(value / 1024 ** 2)}M`;
  if (value >= 1024) return `${Math.round(value / 1024)}K`;
  return `${Math.floor(value)}B`;
};

const bytesToHumanPrecise = (value: number | null | undefined) => {
  if (!Number.isFinite(value) || !value || value <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / Math.pow(1024, idx);
  const digits = scaled >= 10 || idx === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${units[idx]}`;
};

const HotspotUsers: React.FC = () => {
  type ActiveClassMode = 'A' | 'B' | 'DISABLE_ALL_EXCEPT_DEFAULT_TRIAL';
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [users, setUsers] = useState<HotspotUser[]>([]);
  const [profiles, setProfiles] = useState<HotspotProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [editingUser, setEditingUser] = useState<HotspotUser | null>(null);
  const [formValues, setFormValues] = useState({
    username: '',
    password: '',
    profile: '',
    limitTotalHuman: '',
    turma: 'A',
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [activeTurma, setActiveTurma] = useState<ActiveClassMode>('A');
  const [applyingActiveTurma, setApplyingActiveTurma] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const filteredUsers = useMemo(() => {
    if (statusFilter === 'active') return users.filter((u) => !u.disabled);
    if (statusFilter === 'inactive') return users.filter((u) => u.disabled === true);
    return users;
  }, [users, statusFilter]);

  const selectedBranchLabel = useMemo(() => {
    const branch = branches.find((b) => b.id === selectedVesselId);
    if (!branch) return 'Selecione um vessel';
    return branch.company?.name ? `${branch.name} - ${branch.company.name}` : branch.name;
  }, [branches, selectedVesselId]);

  const loadBranches = useCallback(async () => {
    try {
      const res = await api.get('/branches');
      const data = res.data || [];
      setBranches(data);
      if (!selectedVesselId && data.length > 0) {
        setSelectedVesselId(data[0].id);
      }
    } catch (err) {
      toast.error('Falha ao carregar vessels');
    }
  }, [selectedVesselId]);

  const loadHotspotData = useCallback(async (vesselId: string) => {
    if (!vesselId) return;
    setLoading(true);
    setError(null);
    try {
      const [usersRes, profilesRes, dailyRes] = await Promise.all([
        api.get(`/vessels/${vesselId}/mikrotik/hotspot/users`),
        api.get(`/vessels/${vesselId}/mikrotik/hotspot/profiles`),
        api.get(`/vessels/${vesselId}/mikrotik/hotspot/users/daily-consumption`)
      ]);

      const dailyMap = new Map<string, number>(
        ((dailyRes.data?.items || []) as DailyConsumptionItem[]).map((item) => [
          item.username,
          Number(item.bytesToday) || 0
        ])
      );

      setUsers(
        (usersRes.data || []).map((user: HotspotUser) => ({
          ...user,
          bytesToday: dailyMap.get(user.username) || 0
        }))
      );
      setProfiles(profilesRes.data || []);
    } catch (err: any) {
      setError('Falha ao carregar usuários do hotspot.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (selectedVesselId) {
      loadHotspotData(selectedVesselId);
    }
  }, [loadHotspotData, selectedVesselId]);

  const openCreate = () => {
    setModalMode('create');
    setEditingUser(null);
    setFormValues({
      username: '',
      password: '',
      profile: '',
      limitTotalHuman: '',
      turma: 'A',
      isActive: true
    });
    setModalOpen(true);
  };

  const openEdit = (user: HotspotUser) => {
    setModalMode('edit');
    setEditingUser(user);
    const turmaValue =
      user.comment === 'TURMA A' ? 'A' : user.comment === 'TURMA B' ? 'B' : 'A';
    setFormValues({
      username: user.username,
      password: '',
      profile: user.profile,
      limitTotalHuman: bytesToHumanInput(user.limitBytesTotal ?? null),
      turma: turmaValue,
      isActive: user.disabled === true ? false : true
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const validateForm = () => {
    const username = formValues.username.trim();
    if (username.length < 3) {
      toast.error('Nome deve ter no mínimo 3 caracteres.');
      return false;
    }
    if (modalMode === 'create' && formValues.password.trim().length === 0) {
      toast.error('Senha é obrigatória ao criar usuário.');
      return false;
    }
    if (!formValues.profile) {
      toast.error('Perfil é obrigatório.');
      return false;
    }
    if (!['A', 'B'].includes(formValues.turma)) {
      toast.error('Turma é obrigatória.');
      return false;
    }
    if (formValues.limitTotalHuman.trim().length > 0) {
      const parsed = humanToBytes(formValues.limitTotalHuman);
      if (parsed === undefined) {
        toast.error('Limite total inválido. Use apenas números com K, M ou GB (ex.: 500K, 20M, 2GB).');
        return false;
      }
    }

    const duplicate = users.some(
      (u) =>
        u.username.toLowerCase() === username.toLowerCase() &&
        (modalMode === 'create' || u.id !== editingUser?.id)
    );
    if (duplicate) {
      toast.error('Já existe um usuário com esse nome neste vessel.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVesselId || !validateForm()) return;
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await api.post(`/vessels/${selectedVesselId}/mikrotik/hotspot/users`, {
          username: formValues.username.trim(),
          password: formValues.password,
          profile: formValues.profile,
          limitTotalHuman: formValues.limitTotalHuman.trim() || undefined,
          turma: formValues.turma,
          isActive: formValues.isActive
        });
        toast.success('Usuário criado com sucesso.');
      } else if (editingUser) {
        const payload: any = {
          username: formValues.username.trim(),
          profile: formValues.profile,
          limitTotalHuman: formValues.limitTotalHuman.trim(),
          turma: formValues.turma,
          isActive: formValues.isActive
        };
        if (formValues.password.trim().length > 0) {
          payload.password = formValues.password;
        }
        await api.put(
          `/vessels/${selectedVesselId}/mikrotik/hotspot/users/${editingUser.id}`,
          payload
        );
        toast.success('Usuário atualizado com sucesso.');
      }
      setModalOpen(false);
      await loadHotspotData(selectedVesselId);
    } catch (err: any) {
      const details = err?.response?.data?.details;
      const detailMessage =
        (typeof details === 'string' && details.trim().length > 0 ? details : null) ||
        details?.message ||
        details?.error ||
        null;
      const message =
        detailMessage ||
        err?.response?.data?.error ||
        (modalMode === 'create' ? 'Falha ao criar usuário.' : 'Falha ao atualizar usuário.');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const retry = () => {
    if (selectedVesselId) {
      loadHotspotData(selectedVesselId);
    }
  };

  const applyActiveTurma = async (turma: ActiveClassMode) => {
    if (!selectedVesselId || applyingActiveTurma) return;
    setApplyingActiveTurma(true);
    try {
      const res = await api.post(`/vessels/${selectedVesselId}/mikrotik/hotspot/users/active-class`, {
        turma
      });
      setActiveTurma(turma);
      const failedUpdates = Number(res?.data?.failedUpdates || 0);
      if (turma === 'A' || turma === 'B') {
        if (failedUpdates > 0) {
          toast.error(`Turma ${turma} aplicada com ${failedUpdates} falha(s).`);
        } else {
          toast.success(`Turma ${turma} ativada com sucesso.`);
        }
      } else {
        if (failedUpdates > 0) {
          toast.error(`Regra aplicada com ${failedUpdates} falha(s).`);
        } else {
          toast.success('Todos os usuários foram desativados (exceto default-trial).');
        }
      }
      await loadHotspotData(selectedVesselId);
    } catch (err: any) {
      const details = err?.response?.data?.details;
      const detailMessage =
        (typeof details === 'string' && details.trim().length > 0 ? details : null) ||
        details?.message ||
        details?.error ||
        null;
      const message = detailMessage || err?.response?.data?.error || 'Falha ao aplicar turma ativa.';
      toast.error(message);
    } finally {
      setApplyingActiveTurma(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Controle de Usuários Hotspot</h1>
          <p className="text-sm text-gray-500">
            Gerencie usuários do hotspot para o vessel selecionado.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => loadHotspotData(selectedVesselId)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            disabled={loading || !selectedVesselId}
          >
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button
            onClick={openCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            disabled={!selectedVesselId}
          >
            <Plus size={16} />
            Novo Usuário
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Vessel</label>
        <select
          value={selectedVesselId}
          onChange={(e) => setSelectedVesselId(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {branches.length === 0 && <option value="">Nenhum vessel disponível</option>}
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name} {branch.company?.name ? `- ${branch.company.name}` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-2">{selectedBranchLabel}</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Turma Ativa</label>
        <select
          value={activeTurma}
          onChange={(e) => {
            const value = e.target.value as ActiveClassMode;
            setActiveTurma(value);
            applyActiveTurma(value);
          }}
          disabled={!selectedVesselId || applyingActiveTurma}
          className="w-full md:w-[500px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="A">TURMA A</option>
          <option value="B">TURMA B</option>
          <option value="DISABLE_ALL_EXCEPT_DEFAULT_TRIAL">
            DESATIVAR TODOS (EXCETO DEFAULT-TRIAL)
          </option>
        </select>
        <p className="text-xs text-gray-500 mt-2">
          Ao selecionar, aplica a regra escolhida. O usuário default-trial nunca entra nessas regras.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Status:</span>
        {(['all', 'active', 'inactive'] as const).map((opt) => (
          <button
            key={opt}
            onClick={() => setStatusFilter(opt)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              statusFilter === opt
                ? opt === 'active'
                  ? 'bg-green-600 text-white border-green-600'
                  : opt === 'inactive'
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {opt === 'all' ? 'Todos' : opt === 'active' ? 'Ativos' : 'Inativos'}
            {' '}
            <span className="font-semibold">
              ({opt === 'all' ? users.length : opt === 'active' ? users.filter((u) => !u.disabled).length : users.filter((u) => u.disabled === true).length})
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-10 text-center text-gray-600 space-y-3">
            <AlertTriangle className="mx-auto text-yellow-500" />
            <p>{error}</p>
            <button
              onClick={retry}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-gray-500 space-y-2">
            <Users className="mx-auto text-gray-400" />
            <p>Sem usuários cadastrados neste vessel.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Perfil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Turma
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Limit Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Consumo Hoje
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      Nenhum usuário {statusFilter === 'active' ? 'ativo' : 'inativo'} encontrado.
                    </td>
                  </tr>
                ) : null}
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.profile || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.comment === 'TURMA A'
                        ? 'A'
                        : user.comment === 'TURMA B'
                        ? 'B'
                        : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.disabled ? 'Desativado' : 'Ativo'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {bytesToHumanDisplay(user.limitBytesTotal ?? null)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {bytesToHumanPrecise(user.bytesToday ?? null)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEdit(user)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {modalMode === 'create' ? 'Novo Usuário' : 'Editar Usuário'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formValues.username}
                  onChange={(e) => setFormValues({ ...formValues, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="usuario"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <input
                  type="password"
                  value={formValues.password}
                  onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={modalMode === 'edit' ? 'Deixe em branco para manter' : 'Senha'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                <select
                  value={formValues.profile}
                  onChange={(e) => setFormValues({ ...formValues, profile: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione um perfil</option>
                  {profiles.map((profile) => (
                    <option key={profile.name} value={profile.name}>
                      {profile.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Turma</label>
                <select
                  value={formValues.turma}
                  onChange={(e) => setFormValues({ ...formValues, turma: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="A">TURMA A</option>
                  <option value="B">TURMA B</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Usuário ativo</label>
                  <p className="text-xs text-gray-500">Desative para bloquear o usuário no MikroTik.</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={formValues.isActive}
                    onChange={(e) => setFormValues({ ...formValues, isActive: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:h-5 after:w-5 after:rounded-full after:transition peer-checked:after:translate-x-5" />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Limit Total Bytes (K, M ou GB)
                </label>
                <input
                  type="text"
                  value={formValues.limitTotalHuman}
                  onChange={(e) => setFormValues({ ...formValues, limitTotalHuman: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="2GB"
                />
                {editingUser?.limitBytesTotal && bytesToHumanInput(editingUser.limitBytesTotal) === '' && (
                  <p className="text-xs text-amber-600 mt-1">
                    Limite atual em bytes ({editingUser.limitBytesTotal}) não cabe no padrão K/M/GB. Defina um novo
                    valor para salvar.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : modalMode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotspotUsers;
